import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { Truck, List, Loader2 } from "lucide-react";
import {
  PageHeader,
  Card,
  Pill,
  SectionTitle,
} from "@/components/layout/PageShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import { useReportBranch } from "@/contexts/ReportBranchContext";

import api from "@/lib/api";
import { aed, num } from "@/lib/format";
import { printDeliveryReport } from "@/lib/deliveryReportPrint";
import { cn } from "@/lib/utils";
import { ReportKpiStrip } from "@/components/reports/ReportKpiStrip";
import { ReportTableCard } from "@/components/reports/ReportTableCard";
import ReportExportButtons from "@/components/reports/ReportExportButtons";
import { ReportTrendPanel } from "@/components/reports/ReportTrendPanel";
import PaymentMethodFilter from "@/components/reports/PaymentMethodFilter";
import { formatReportFilterLine } from "@/lib/reportFilters";

const TABS = [
  { id: "summary", label: "Driver Summary", path: "/reports/delivery-summary", icon: Truck },
  { id: "detail", label: "Driver Detail", path: "/reports/delivery-detail", icon: List },
];

const SETTLEMENT_FILTERS = [
  { value: "all", label: "All settlements" },
  { value: "settled", label: "Settled only" },
  { value: "unsettled", label: "Unsettled only" },
];

const ENDPOINTS = {
  summary: "/reports/delivery/persons/summary",
  detail: "/reports/delivery/persons/detail",
};

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const today = () => new Date().toISOString().slice(0, 10);

export default function DeliveryReports() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branchId, allBranches, reportParams, canQuery } = useReportBranch();

  const activeTab = useMemo(
    () => (location.pathname.includes("delivery-detail") ? "detail" : "summary"),
    [location.pathname],
  );

  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [personId, setPersonId] = useState("all");
  const [settlement, setSettlement] = useState("all");
  const [paymentMethod, setPaymentMethod] = useState("all");

  const { data: personsData } = useQuery({
    queryKey: ["delivery-persons", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/delivery-persons", { params: { branch_id: branchId } });
      return res;
    },
    enabled: Boolean(branchId),
  });

  const persons = personsData?.delivery_persons ?? [];
  const detailDriverId = activeTab === "detail" && personId !== "all" ? personId : persons[0]?.id;

  const { data: report, isLoading, isFetching } = useQuery({
    queryKey: ["delivery-report", activeTab, branchId, fromDate, toDate, personId, settlement, detailDriverId, paymentMethod],
    queryFn: async () => {
      const params = {
        ...reportParams,
        from_date: fromDate,
        to_date: toDate,
        settlement: settlement !== "all" ? settlement : undefined,
      };
      if (paymentMethod !== "all") params.payment_method = paymentMethod;
      if (activeTab === "summary" && personId !== "all") {
        params.delivery_person_id = personId;
      }
      if (activeTab === "detail") {
        params.delivery_person_id = detailDriverId;
      }
      const { data: res } = await api.get(ENDPOINTS[activeTab], { params });
      return res;
    },
    enabled: Boolean(canQuery) && (activeTab === "summary" || Boolean(detailDriverId)),
  });

  const chartData = useMemo(() => {
    if (activeTab !== "summary" || !report?.rows) return [];
    return report.rows.slice(0, 12).map((r) => ({
      name: (r.person_name || "—").length > 14 ? `${r.person_name.slice(0, 14)}…` : r.person_name,
      net: r.net_sales,
      pending: r.pending_settlement,
    }));
  }, [report, activeTab]);

  return (
    <div className="min-w-0">
      <PageHeader
        title="Delivery Reports"
        subtitle={
          activeTab === "summary"
            ? "Person-wise delivery summary with settlement status"
            : "Bill-level delivery detail for a selected driver"
        }
        actions={
          <ReportExportButtons
            disabled={!report}
            title="Delivery Reports"
            filename={`delivery-${activeTab}`}
            period={formatReportFilterLine({ fromDate, toDate, paymentMethod })}
            onPrint={() => printDeliveryReport(report, activeTab, { branchId, allBranches })}
          />
        }
      />

      <div className="flex gap-1 mb-4 border-b border-slate-200 overflow-x-auto">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => navigate(tab.path)}
              className={cn(
                "flex items-center gap-1.5 px-4 py-2.5 text-[12.5px] font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                activeTab === tab.id
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-600 hover:text-slate-900",
              )}
            >
              <Icon size={14} /> {tab.label}
            </button>
          );
        })}
      </div>

      <Card className="p-5 mb-4">
        <SectionTitle className="!mb-3">Filters</SectionTitle>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-[12px] text-slate-600 mb-1 block">From</Label>
            <Input type="date" className="h-9 w-[160px]" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div>
            <Label className="text-[12px] text-slate-600 mb-1 block">To</Label>
            <Input type="date" className="h-9 w-[160px]" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div>
            <Label className="text-[12px] text-slate-600 mb-1 block">Driver</Label>
            <Select
              value={activeTab === "detail" ? String(detailDriverId || "") : personId}
              onValueChange={(v) => setPersonId(v)}
            >
              <SelectTrigger className="h-9 w-[220px]">
                <SelectValue placeholder={activeTab === "detail" ? "Select driver" : "All drivers"} />
              </SelectTrigger>
              <SelectContent>
                {activeTab === "summary" && <SelectItem value="all">All drivers</SelectItem>}
                {persons.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.code} — {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[12px] text-slate-600 mb-1 block">Settlement</Label>
            <Select value={settlement} onValueChange={setSettlement}>
              <SelectTrigger className="h-9 w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SETTLEMENT_FILTERS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <PaymentMethodFilter value={paymentMethod} onChange={setPaymentMethod} />
          {(isLoading || isFetching) && (
            <Pill className="mb-1"><Loader2 size={12} className="animate-spin mr-1" /> Loading…</Pill>
          )}
        </div>
      </Card>

      {report && (
        <>
          <SummaryCards report={report} activeTab={activeTab} />

          {activeTab === "summary" ? (
            <SummaryTable rows={report.rows} />
          ) : (
            <DetailTable bills={report.bills} driver={report.driver} />
          )}

          {chartData.length > 0 && (
            <ReportTrendPanel title="Net sales by driver (optional)">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={56} />
                  <Tooltip formatter={(v) => aed(v)} />
                  <Bar dataKey="net" fill="#F36F21" name="Net sales" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ReportTrendPanel>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCards({ report, activeTab }) {
  const s = report.summary || {};
  const items =
    activeTab === "summary"
      ? [
          { label: "Drivers", value: num(s.driver_count) },
          { label: "Bills", value: num(s.bill_count) },
          { label: "Parcel", value: aed(s.parcel_charges) },
          { label: "Round off", value: aed(s.round_off) },
          { label: "Net sales", value: aed(s.net_sales), emphasize: true },
          { label: "Cash", value: aed(s.cash_sales) },
          { label: "Pending settlement", value: aed(s.pending_settlement) },
        ]
      : [
          { label: "Driver", value: s.driver_name || report.driver?.name || "—" },
          { label: "Bills", value: num(s.bill_count) },
          { label: "Round off", value: aed(s.round_off) },
          { label: "Net sales", value: aed(s.net_sales), emphasize: true },
          { label: "Cash", value: aed(s.cash_sales) },
          { label: "Settled", value: num(s.settled_count) },
          { label: "Pending", value: aed(s.pending_settlement) },
        ];

  return <ReportKpiStrip items={items} />;
}

function SummaryTable({ rows }) {
  return (
    <ReportTableCard title="By driver">
      <table>
        <thead>
          <tr>
            <th className="text-left">Code</th>
            <th className="text-left">Driver</th>
            <th className="text-right">Bills</th>
            <th className="text-right">Parcel</th>
            <th className="text-right">Round off</th>
            <th className="text-right">Net</th>
            <th className="text-right">Cash</th>
            <th className="text-right">Delivered</th>
            <th className="text-right">Settled</th>
            <th className="text-right">Pending</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r) => (
            <tr key={r.delivery_person_id ?? r.person_name}>
              <td className="font-mono text-[12px]">{r.person_code || "—"}</td>
              <td>{r.person_name}</td>
              <td className="text-right">{r.bill_count}</td>
              <td className="text-right font-mono">{aed(r.parcel_charge)}</td>
              <td className="text-right font-mono">{aed(r.round_off)}</td>
              <td className="text-right font-mono font-medium">{aed(r.net_sales)}</td>
              <td className="text-right font-mono">{aed(r.cash_sales)}</td>
              <td className="text-right">{r.delivered_count}</td>
              <td className="text-right">{r.settled_count}</td>
              <td className="text-right font-mono text-amber-800">{aed(r.pending_settlement)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ReportTableCard>
  );
}

function DetailTable({ bills, driver }) {
  return (
    <ReportTableCard title={`Bills — ${driver?.code || ""} ${driver?.name || ""}`.trim()}>
      <table>
        <thead>
          <tr>
            <th className="text-left">Bill</th>
            <th className="text-left">Paid</th>
            <th className="text-left">Customer</th>
            <th className="text-left">TRN</th>
            <th className="text-left">Area</th>
            <th className="text-left">Status</th>
            <th className="text-left">Settled</th>
            <th className="text-left">Pay</th>
            <th className="text-right">Round off</th>
            <th className="text-right">Net</th>
          </tr>
        </thead>
        <tbody>
          {(bills || []).map((b) => (
            <tr key={b.id}>
              <td className="font-mono text-[12px]">{b.bill_number}</td>
              <td>
                {b.paid_date} {b.paid_time}
              </td>
              <td>{b.customer_name}</td>
              <td className="font-mono text-[12px]">{b.customer_tax_number || "—"}</td>
              <td>{b.delivery_area || "—"}</td>
              <td>{b.delivery_status_label || "—"}</td>
              <td>
                <Pill tone={b.delivery_settled ? "green" : "amber"}>
                  {b.delivery_settled ? "Yes" : "No"}
                </Pill>
              </td>
              <td className="uppercase text-[11px]">{b.payment_method || "—"}</td>
              <td className="text-right font-mono">{aed(b.round_off)}</td>
              <td className="text-right font-mono font-medium">{aed(b.net_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ReportTableCard>
  );
}
